<template>
    <div
        v-if="mapId"
        :id="mapId"
        class="map-element"
        :style="{ height: `${element.height}px` }"
    />
</template>

<script>

    import 'leaflet/dist/leaflet';
    import { generateId } from '@nodes/helpers/string';

    export default {

        name: 'YFormMap',

        props: {
            /**
             * Element of map
             */
            element: Object,

            /**
             * Input Value
             */
            value: {
                type   : [Array, Object],
                default: () => [],
            },

            /**
             * Enable Multiple Marker feature for components
             */
            multiple: {
                type   : Boolean,
                default: false,
            },
        },

        /**
         * @inheritDoc
         */
        data() {
            return {
                model : this.multiple ? this.element.model : this.value,
                center: null,

                default: {
                    center: {
                        lat : 35.7001388,
                        long: 51.4033774,
                    },
                },

                deleteMode: false,
                mapId     : null,
                showFields: false,
            };
        },

        computed: {

            /**
             * Marker Position
             *
             * @returns {null|never|string[]}
             */
            markerPosition() {
                return this.element.marker ? this.element.marker.split(',') : null;
            },

            /**
             * Popup layout
             *
             * @returns {*}
             */
            popupLayout() {
                return this.element.this.popup || null;
            },

        },

        watch: {
            /**
             * Watch model and emit
             */
            model() {
                this.$emit('input', this.model);
            },
        },

        /**
         * @inheritDoc
         */
        beforeMount() {
            this.mapId = generateId();
            this.$wait.end('handle-map');
        },

        /**
         * @inheritDoc
         */
        mounted() {
            this.setCenter();
            this.initMap();
        },

        methods: {

            /**
             * Set default value for marker & reset map to default
             */
            resetMarker() {
                // eslint-disable-next-line vue/no-mutating-props
                this.value.lat = this.element.value ? this.element.value.lat : this.default.center.lat;
                // eslint-disable-next-line vue/no-mutating-props
                this.value.long = this.element.value ? this.element.value.long : this.default.center.long;
                setTimeout(() => this.mapRefresh(), 3000);
            },

            /**
             * Refresh map
             */
            mapRefresh() {
                try {
                    this.$wait.start('handle-map');

                    // Set new value for lat & long
                    // eslint-disable-next-line vue/no-mutating-props
                    this.value.lat = this.formatLocation(this.value.lat.toString()
                        .trim());
                    // eslint-disable-next-line vue/no-mutating-props
                    this.value.long = this.formatLocation(this.value.long.toString()
                        .trim());

                    // Generate new map id
                    const id = generateId();

                    // Set new map element
                    document.getElementById(this.mapId).innerHTML = `<div class="map-element" id="${id}" style="height: ${this.element.height}px"></div>`;

                    // Assign new map id in global variable
                    this.mapId = id;

                    // Initial new map
                    this.setCenter();
                    this.initMap();

                    this.$wait.end('handle-map');
                } catch {
                    this.$toast.error(this.$t('fields.error.invalid'));
                    this.resetMarker();
                }
            },

            /**
             * Coordinates type (DMS format should be like this => DD°MM'SS.S"E or DD°MM'SS.S")
             *
             * @param {object} location
             * @returns {string}
             */
            coordinatesType(location) {
                const v = location.toString();
                if ((v.includes('°') || v.toLowerCase()
                    .includes('d')) && v.includes('\'') && v.includes('"')) {
                    return 'DMS';
                }
                return 'DD';
            },

            /**
             * Convert DMS to DD
             *
             * @param {string} degrees
             * @param {string} minutes
             * @param {string} seconds
             * @param {string} direction
             * @returns {string}
             */
            ConvertDMSToDD(degrees, minutes, seconds, direction) {
                let dd = parseInt(degrees, 10) + (parseInt(minutes, 10) + parseFloat(seconds / 60)) / 60;

                if (direction.toLowerCase() === 's' || direction.toLowerCase() === 'w') {
                    dd *= -1;
                } // Don't do anything for N or E

                return dd;
            },

            /**
             * Set Center of the map
             */
            setCenter() {
                if (this.multiple) {
                    this.center = {
                        lat : this.element.value ? this.element.value.lat : this.default.center.lat,
                        long: this.element.value ? this.element.value.long : this.default.center.long,
                    };
                } else {
                    this.center = {
                        lat : (this.value && this.value.lat) ? this.value.lat : this.default.center.lat,
                        long: (this.value && this.value.long) ? this.value.long : this.default.center.long,
                    };
                }
            },

            /**
             * Init map
             */
            initMap() {
                const id = this.mapId;

                const self = this;

                let marker; // eslint-disable-line

                const map = L.map(id) // eslint-disable-line
                    .setView(Object.values(this.center), this.element.zoom || 17); // eslint-disable-line no-undef

                const markerImage = L.icon({ // eslint-disable-line no-undef
                    iconUrl   : require('./assets/map-marker.png'), // eslint-disable-line global-require
                    iconSize  : [25, 41],
                    iconAnchor: [13, 41],

                });

                /**
                 * Get user location from the browser if there is no marker
                 */
                if (!this.model) {
                    map.locate({ setView: true });
                }

                /**
                 * Drag the marker and update its latlng
                 *
                 * @param {object} e
                 */
                function dragMarker(e) {
                    let index;
                    self.model.map((i) => { // eslint-disable-line
                        if (i.id === e.target.options.id) {
                            index = self.model.indexOf(i);
                            self.model[index].lat = e.target._latlng.lat;
                            self.model[index].long = e.target._latlng.lng;
                            return index;
                        }
                    });
                }

                /**
                 * When user location found, pan the map to the user location
                 * (pan: make the user location the center of the map)
                 *
                 * @param {object} e
                 */
                function onLocationFound(e) { // eslint-disable-line
                    map.panTo(new L.LatLng(e.latlng.lat, e.latlng.lng)); // eslint-disable-line
                }

                /**
                 * If user location can't be found or user deny access to the browser location, Shows Error
                 *
                 * @param {object} e
                 */
                function onLocationError(e) { // eslint-disable-line
                    // console.log('We can not access to your location', e);
                }

                L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { // eslint-disable-line no-undef
                    attribution: '',
                })
                    .addTo(map);

                if (this.multiple) {
                    /**
                     * Place saved marker on the map
                     */
                    this.element.model.forEach((model) => {
                        const markerPos = {
                            id : model.id,
                            lat: model.lat,
                            lng: model.long,
                        };
                        marker = L.marker(markerPos, { // eslint-disable-line no-undef
                            icon     : markerImage,
                            draggable: true,
                            id       : markerPos.id,
                        })
                            .addTo(map)
                            .on('dragend', dragMarker);
                    });

                    /**
                     * Add new marker when user clicked on the map
                     */
                    map.on('click', (e) => { // eslint-disable-line
                        marker = L.marker(e.latlng, { // eslint-disable-line no-undef
                            icon     : markerImage,
                            draggable: true,
                        })
                            .addTo(map)
                            .on('dragend', dragMarker);
                        self.model.push(e.latlng);
                    });
                }

                if (!this.multiple) {
                    let markerPos;
                    if (this.model && this.model.length > 0) {
                        markerPos = {
                            lat: this.model.lat,
                            lng: this.model.long,
                        };
                    } else {
                        markerPos = {
                            lat: this.center.lat,
                            lng: this.center.long,
                        };
                    }

                    marker = L.marker(markerPos, { // eslint-disable-line no-undef
                        icon: markerImage,
                    })
                        .addTo(map);

                    map.on('move', () => {
                        marker.setLatLng(map.getCenter());
                    });

                    map.on('dragend', () => {
                        const position = Object.values(marker.getLatLng());
                        this.$emit('input', {
                            lat : position[0],
                            long: position[1],
                        });
                    });
                }

                map.on('locationfound', onLocationFound);
                map.on('locationerror', onLocationError);
            },

        },

    };
</script>

<style lang="scss" src="./index.scss" />
